import { RoasteryAuthProvider } from "@roastery/auth";
import { I18nextProvider, createI18n, isSupportedLanguage } from "@roastery/i18n";
import { Toaster } from "@roastery/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary, RouteErrorScreen, initErrorTracking } from "@roastery/observability";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { routeTree } from "./routeTree.gen";
import { runtimeConfig } from "./runtime-config";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A 401 or 403 will not become a 200 by asking again; neither will the 402 the plan raises,
      // which the operator's panel can meet when it reads a roastery's entitlements.
      retry: (failureCount, error) => failureCount < 2 && !isAuthError(error),
    },
  },
});

function isAuthError(error: unknown): boolean {
  const status = (error as { status?: number } | undefined)?.status;
  return status === 401 || status === 403 || status === 402;
}

// Awaited, because the dictionary is a chunk of its own since US-37.8: rendering first and
// translating afterwards would paint the keys.
const i18n = await createI18n(
  isSupportedLanguage(runtimeConfig.defaultLocale) ? runtimeConfig.defaultLocale : "pl",
);

// The server answers in the language we ask for (US-23.4) - a product's description, a validation
// message, a problem detail. A cached answer is keyed by method and path, not by the header, so a
// change of language makes every one of them stale.
i18n.on("languageChanged", () => void queryClient.invalidateQueries());

const errorLabels = () => ({
  title: i18n.t("errors.boundary.title"),
  description: i18n.t("errors.boundary.description"),
  eventId: i18n.t("errors.boundary.eventId"),
  reload: i18n.t("errors.boundary.reload"),
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // A screen that throws lands here, not in the boundary around the router (US-18.9): the router wraps
  // every route in its own. Same screen, same event in GlitchTip.
  defaultErrorComponent: ({ error }) => <RouteErrorScreen error={error} labels={errorLabels} />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("#root element is missing from index.html");
}

// Errors as events (US-18.9): before the first render, so a throw during it is the first event.
initErrorTracking({
  dsn: runtimeConfig.sentryDsn,
  environment: runtimeConfig.environment,
  release: runtimeConfig.release,
  app: "platform-admin",
});

createRoot(rootElement).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <RoasteryAuthProvider
          authority={runtimeConfig.oidcAuthority}
          clientId={runtimeConfig.oidcClientId}
          apiBaseUrl={runtimeConfig.apiUrl}
          // Signing out lands on a page that says so, not on the login screen just left (US-23.6).
          postLogoutRedirectUri={`${window.location.origin}/signed-out`}
          // The language the screens are read in, so the server answers in it (US-23.4): a product's
          // description, a validation message and a problem detail all come from Accept-Language.
          getLanguage={() => i18n.resolvedLanguage}
          // ...and so does the sign-in screen (US-23.1), which is a different server and a different
          // mechanism: `ui_locales` on the authorization request.
          uiLocales={i18n.resolvedLanguage}
        >
          <ErrorBoundary labels={errorLabels}>
            <RouterProvider router={router} />
          </ErrorBoundary>
          <Toaster />
        </RoasteryAuthProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
